import type {
  ProjectDrawingOfflineArtifact,
  ProjectDrawingOfflineDelivery,
  ProjectDrawingViewerBootstrap,
} from '../types';
import {
  ARTIFACT_STORE,
  BOOTSTRAP_STORE,
  DrawingOfflineProgress,
  LocalDrawingOfflineRecord,
  OFFLINE_DB_NAME,
  OPFS_ROOT,
  artifactKey,
  getArtifactRecordByKey,
  getFileHandle,
  openDatabase,
  putArtifactRecord,
  removeOfflineDrawingEntry,
  requestResult,
  resetDatabaseConnection,
  transactionComplete,
  validateArtifact,
} from './drawingOfflineStorage';

export type { DrawingOfflineDownloadState, DrawingOfflineProgress, LocalDrawingOfflineRecord } from './drawingOfflineStorage';

const bootstrapKeyOf = (userId: number, projectId: number, drawingId: number, revisionId: number): string =>
  `${userId}:${projectId}:${drawingId}:${revisionId}`;

interface LocalDrawingBootstrapRecord {
  key: string;
  userId: number;
  projectId: number;
  drawingId: number;
  revisionId: number;
  bootstrap: ProjectDrawingViewerBootstrap;
  savedAt: number;
}

export interface LocalDrawingOfflineProject {
  projectId: number;
  projectName: string;
  drawingCount: number;
  storedBytes: number;
}

export interface DownloadDrawingOfflineOptions {
  userId: number;
  artifact: ProjectDrawingOfflineArtifact;
  accessExpiresAt: string;
  resolveDelivery: () => Promise<ProjectDrawingOfflineDelivery>;
  signal?: AbortSignal;
  onProgress?: (progress: DrawingOfflineProgress) => void;
}

// The chunked-download loop itself runs in a dedicated Worker (see
// drawingOfflineWorker.ts / Phase 1c of the Drawings stabilization plan) so a
// large offline package download can't jank the UI thread. This module stays
// the public API and orchestrates that worker via postMessage; everything
// else (listing, reading, cleanup) is quick enough to stay on the main
// thread and just reuses the same shared storage helpers the worker uses.

let worker: Worker | null = null;
let requestCounter = 0;
const activeDownloads = new Map<string, Promise<LocalDrawingOfflineRecord>>();

type WorkerOutboundMessage =
  | { type: 'delivery-request'; requestId: string; deliveryRequestId: string }
  | { type: 'progress'; requestId: string; progress: DrawingOfflineProgress }
  | { type: 'done'; requestId: string; record: LocalDrawingOfflineRecord }
  | { type: 'error'; requestId: string; message: string; name?: string };

function getWorker(): Worker {
  if (!worker) {
    worker = new Worker(new URL('./drawingOfflineWorker.ts', import.meta.url), { type: 'module' });
  }
  return worker;
}

function runDownloadOnWorker(options: DownloadDrawingOfflineOptions): Promise<LocalDrawingOfflineRecord> {
  const requestId = `${Date.now()}-${(requestCounter += 1)}`;
  const activeWorker = getWorker();

  return new Promise<LocalDrawingOfflineRecord>((resolve, reject) => {
    const onAbort = () => activeWorker.postMessage({ type: 'abort', requestId });
    options.signal?.addEventListener('abort', onAbort);

    const cleanup = () => {
      activeWorker.removeEventListener('message', onMessage);
      options.signal?.removeEventListener('abort', onAbort);
    };

    const onMessage = (event: MessageEvent<WorkerOutboundMessage>) => {
      const message = event.data;
      if (message.requestId !== requestId) return;
      if (message.type === 'delivery-request') {
        options.resolveDelivery()
          .then((delivery) => activeWorker.postMessage({ type: 'delivery-response', requestId, deliveryRequestId: message.deliveryRequestId, delivery }))
          .catch((error: any) => activeWorker.postMessage({
            type: 'delivery-response', requestId, deliveryRequestId: message.deliveryRequestId, error: error?.message || 'Could not resolve a download link',
          }));
        return;
      }
      if (message.type === 'progress') {
        options.onProgress?.(message.progress);
        return;
      }
      if (message.type === 'done') {
        cleanup();
        resolve(message.record);
        return;
      }
      if (message.type === 'error') {
        cleanup();
        reject(Object.assign(new Error(message.message), { name: message.name }));
      }
    };

    activeWorker.addEventListener('message', onMessage);
    activeWorker.postMessage({
      type: 'start',
      requestId,
      userId: options.userId,
      artifact: options.artifact,
      accessExpiresAt: options.accessExpiresAt,
    });
  });
}

export function supportsManagedDrawingOffline(): boolean {
  return typeof window !== 'undefined'
    && Boolean(window.indexedDB)
    && Boolean(navigator.storage?.getDirectory)
    && Boolean(navigator.serviceWorker)
    && Boolean(window.crypto?.subtle)
    && typeof Worker !== 'undefined';
}

export async function estimateDrawingOfflineStorage(): Promise<StorageEstimate> {
  if (!navigator.storage?.estimate) return {};
  return navigator.storage.estimate();
}

export function downloadDrawingOffline(options: DownloadDrawingOfflineOptions): Promise<LocalDrawingOfflineRecord> {
  validateArtifact(options.artifact);
  const key = artifactKey(options.userId, options.artifact.artifact_id);
  const active = activeDownloads.get(key);
  if (active) return active;
  const download = runDownloadOnWorker(options).finally(() => {
    if (activeDownloads.get(key) === download) activeDownloads.delete(key);
  });
  activeDownloads.set(key, download);
  return download;
}

export async function listOfflineDrawings(userId: number): Promise<LocalDrawingOfflineRecord[]> {
  const database = await openDatabase();
  const transaction = database.transaction(ARTIFACT_STORE, 'readonly');
  const index = transaction.objectStore(ARTIFACT_STORE).index('userId');
  const records = await requestResult(index.getAll(IDBKeyRange.only(userId))) as LocalDrawingOfflineRecord[];
  await transactionComplete(transaction);
  return records.sort((a, b) => b.updatedAt - a.updatedAt);
}

export async function listAvailableOfflineDrawingProjects(userId: number): Promise<LocalDrawingOfflineProject[]> {
  const records = await listOfflineDrawings(userId);
  const projects = new Map<number, {
    name: string;
    drawingIds: Set<number>;
    storedBytes: number;
  }>();

  for (const record of records) {
    if (record.state !== 'available') continue;
    const projectId = Number(record.artifact.project_id);
    if (!Number.isInteger(projectId) || projectId <= 0) continue;
    const current = projects.get(projectId) || {
      name: record.artifact.project_name || `Project ${projectId}`,
      drawingIds: new Set<number>(),
      storedBytes: 0,
    };
    current.drawingIds.add(Number(record.artifact.drawing_id));
    current.storedBytes += Number(record.artifact.file_size || 0);
    projects.set(projectId, current);
  }

  return Array.from(projects, ([projectId, project]) => ({
    projectId,
    projectName: project.name,
    drawingCount: project.drawingIds.size,
    storedBytes: project.storedBytes,
  })).sort((left, right) => left.projectName.localeCompare(right.projectName));
}

export async function findAvailableOfflineDrawing(
  userId: number,
  projectId: number,
  drawingId: number,
  revisionId?: number,
): Promise<LocalDrawingOfflineRecord | null> {
  const records = await listOfflineDrawings(userId);
  const matches = records.filter((record) => (
    record.state === 'available'
    && record.artifact.project_id === projectId
    && record.artifact.drawing_id === drawingId
    && (revisionId == null || record.artifact.drawing_revision_id === revisionId)
  ));
  if (revisionId != null) return matches[0] || null;
  return matches.find((record) => record.artifact.is_current) || matches[0] || null;
}

export async function openOfflineDrawingFile(record: LocalDrawingOfflineRecord): Promise<File> {
  if (record.state !== 'available') throw new Error('The drawing is not fully available offline');
  const handle = await getFileHandle(record, false);
  const file = await handle.getFile();
  if (file.size !== record.artifact.file_size) {
    throw new Error('The local drawing file is incomplete');
  }
  await putArtifactRecord({ ...record, lastOpenedAt: Date.now(), updatedAt: Date.now() }).catch(() => {});
  return file;
}

export async function removeOfflineDrawing(userId: number, artifactId: string): Promise<void> {
  await removeOfflineDrawingEntry(userId, artifactId);
}

export async function setOfflineDrawingKeepOffline(
  userId: number,
  artifactId: string,
  keepOffline: boolean,
): Promise<void> {
  const record = await getArtifactRecordByKey(artifactKey(userId, artifactId));
  if (!record) return;
  await putArtifactRecord({ ...record, keepOffline, updatedAt: Date.now() });
}

export async function setOfflineDrawingFreshness(
  userId: number,
  artifactId: string,
  revisionFreshness: ProjectDrawingOfflineArtifact['revision_freshness'],
  isCurrent: boolean,
): Promise<LocalDrawingOfflineRecord | null> {
  const record = await getArtifactRecordByKey(artifactKey(userId, artifactId));
  if (!record) return null;
  const updated = {
    ...record,
    artifact: { ...record.artifact, revision_freshness: revisionFreshness, is_current: isCurrent },
    updatedAt: Date.now(),
  };
  await putArtifactRecord(updated);
  return updated;
}

export async function cacheDrawingViewerBootstrap(
  userId: number,
  bootstrap: ProjectDrawingViewerBootstrap,
): Promise<void> {
  const record: LocalDrawingBootstrapRecord = {
    key: bootstrapKeyOf(userId, bootstrap.drawing.project_id, bootstrap.drawing.id, bootstrap.openRevisionId),
    userId,
    projectId: bootstrap.drawing.project_id,
    drawingId: bootstrap.drawing.id,
    revisionId: bootstrap.openRevisionId,
    bootstrap,
    savedAt: Date.now(),
  };
  const database = await openDatabase();
  const transaction = database.transaction(BOOTSTRAP_STORE, 'readwrite');
  transaction.objectStore(BOOTSTRAP_STORE).put(record);
  await transactionComplete(transaction);
}

export async function readCachedDrawingViewerBootstrap(
  userId: number,
  projectId: number,
  drawingId: number,
  revisionId: number,
): Promise<ProjectDrawingViewerBootstrap | null> {
  const database = await openDatabase();
  const transaction = database.transaction(BOOTSTRAP_STORE, 'readonly');
  const record = await requestResult(
    transaction.objectStore(BOOTSTRAP_STORE).get(bootstrapKeyOf(userId, projectId, drawingId, revisionId)),
  ) as LocalDrawingBootstrapRecord | undefined;
  await transactionComplete(transaction);
  return record?.bootstrap || null;
}

export async function clearManagedDrawingOfflineData(userId?: number): Promise<void> {
  if (typeof window === 'undefined' || !window.indexedDB) return;
  if (userId != null) {
    const database = await openDatabase().catch(() => null);
    if (database) {
      const records = await listOfflineDrawings(userId).catch(() => []);
      const transaction = database.transaction([ARTIFACT_STORE, BOOTSTRAP_STORE], 'readwrite');
      const artifactStore = transaction.objectStore(ARTIFACT_STORE);
      const bootstrapStore = transaction.objectStore(BOOTSTRAP_STORE);
      records.forEach((record) => artifactStore.delete(record.key));
      const bootstrapKeys = await requestResult(bootstrapStore.index('userId').getAllKeys(IDBKeyRange.only(userId)));
      bootstrapKeys.forEach((key) => bootstrapStore.delete(key));
      await transactionComplete(transaction);
    }
    const storageRoot = await navigator.storage?.getDirectory?.().catch(() => null);
    const drawingsRoot = storageRoot ? await storageRoot.getDirectoryHandle(OPFS_ROOT).catch(() => null) : null;
    if (drawingsRoot) await drawingsRoot.removeEntry(`user-${userId}`, { recursive: true }).catch(() => {});
    return;
  }

  const database = await openDatabase().catch(() => null);
  database?.close();
  resetDatabaseConnection();
  worker?.terminate();
  worker = null;
  await new Promise<void>((resolve) => {
    const request = window.indexedDB.deleteDatabase(OFFLINE_DB_NAME);
    request.onsuccess = () => resolve();
    request.onerror = () => resolve();
    request.onblocked = () => resolve();
  });
  const storageRoot = await navigator.storage?.getDirectory?.().catch(() => null);
  if (storageRoot) await storageRoot.removeEntry(OPFS_ROOT, { recursive: true }).catch(() => {});
}
